import type { TaskComment } from '@frontend/api-types';
import { dateFormatters, extractSecureNoteIdFromUrl, extractSecureNoteTitleFromHtml } from '@frontend/duck-tape';
import { Fragment, useFeatureIsOn, useState } from '@frontend/react';
import { useVisibilityController } from '@frontend/react/hooks';
import { RenderHTML, XStack, YStack } from '@frontend/web-react';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import { UpdateSecureNoteModal } from '@frontend/web/components';

export type TaskChatWhisperProps = Pick<TaskComment, 'body'> & {
  createdAt?: DateTimeISOString;
  isLineDotted?: boolean;
  isLineVisible?: boolean;
  taskId?: string;
};

export const TaskChatWhisper = ({ body, createdAt, isLineDotted, isLineVisible, taskId }: TaskChatWhisperProps) => {
  const { enabled: isChannelWhispersEnabled } = useFeatureIsOn('is_channel_whispers_enabled');
  const [isSecureNoteCardModalOpen, { close: closeSecureNoteModal, open: openSecureNoteModal }] =
    useVisibilityController();
  const [secureNoteInfo, setSecureNoteInfo] = useState<{ id: string; title: string }>();

  const handleAnchorClick = (href: string) => {
    if (href.includes('secure-note')) {
      const noteId = extractSecureNoteIdFromUrl(href);
      if (noteId) {
        const title = extractSecureNoteTitleFromHtml(body, noteId);
        setSecureNoteInfo({ id: noteId, title: title || '' });
        openSecureNoteModal();
      }
    } else {
      window.open(href, '_blank');
    }
  };

  if (isChannelWhispersEnabled) {
    return (
      <Fragment>
        <YStack className="gap-xs">
          <XStack className="gap-sm items-center">
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <XStack className="size-[9px] rounded-full border-1 border-borderTertiary" />
            <RenderHTML
              anchorClassName="text-p-md text-textPrimary flex items-center"
              className="flex items-center"
              html={`<section class="flex flex-wrap items-center">${body.replaceAll('zd-comment', safeCss('text-p-md text-textTertiary flex items-center'))}<small class="text-p-sm text-neutral-400"> at ${createdAt ? dateFormatters.time(createdAt) : ''}</small></section>`}
              onAnchorClick={handleAnchorClick}
            />
          </XStack>
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value,tailwindcss/no-custom-classname */}
          {isLineVisible ? (
            <YStack
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className={concatClasses(
                'h-[24px] w-[1px] border-neutral-300 border-l-1 ml-[4px]',
                applyClass(isLineDotted, 'border-dashed'),
              )}
            />
          ) : null}
        </YStack>
        {secureNoteInfo?.id ? (
          <UpdateSecureNoteModal
            isOpen={isSecureNoteCardModalOpen}
            onClose={closeSecureNoteModal}
            secureNoteId={secureNoteInfo.id}
            taskId={taskId}
            title={secureNoteInfo.title}
          />
        ) : null}
      </Fragment>
    );
  }

  return (
    <YStack alignItems="center">
      <RenderHTML html={body.replaceAll('zd-comment', safeCss('text-caption text-textTertiary'))} />
    </YStack>
  );
};
